import Grid from '@mui/material/Grid';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import type { AdPlacement } from '@openx/types/adUnit';
import type { DistributionChannel } from '@openx/types/distributionChannel';
import { type FormatTypeData, InventoryFormatTypeOption } from '@openx/types/targeting/formatType';
import {
  type DisplayContextsType,
  displayContexts,
  type TargetingCustomOptions,
} from '@openx/types/targeting/targeting';
import { Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext';
import { DrawerTopBox } from '../shared/TargetingDrawer/DrawerTopBox';
import { FormatTypeGroupOptionDropdown } from './components/FormatTypeGroupOptionDropdown';

interface TopBoxProps {
  distributionChannel: DistributionChannel | null;
  adPlacement: AdPlacement | null;
  targetingParams: FormatTypeData | null;
  setDistributionChannel: (value: DistributionChannel | null) => void;
  setAdPlacement: (value: AdPlacement | null) => void;
  updateParams: (value: FormatTypeData | null) => void;
  customOptions?: TargetingCustomOptions<InventoryFormatTypeOption>;
  displayContext?: DisplayContextsType;
}

export const TopBox = ({
  targetingParams,
  distributionChannel,
  adPlacement,
  setAdPlacement,
  setDistributionChannel,
  updateParams,
  customOptions,
  displayContext,
}: TopBoxProps) => {
  const { t } = useTranslation();
  const { useOldAdPlacementOptions, useOldDistributionChannelOptions } = useTargetingContext();
  const { isAdPlacementLoading, adPlacement: adPlacementMap } = useOldAdPlacementOptions();
  const { isDistributionChannelLoading, distributionChannel: distributionChannelMap } =
    useOldDistributionChannelOptions();

  const [categoryType, setCategoryType] = useState<InventoryFormatTypeOption>(
    InventoryFormatTypeOption.DISTRIBUTION_CHANNEL
  );
  const distributionChannelOptionsList = useMemo(
    () => produceSortedOptionsKeys(distributionChannelMap),
    [distributionChannelMap]
  );

  const adPlacementOptionsList = useMemo(() => produceSortedOptionsKeys(adPlacementMap), [adPlacementMap]);

  const handleDistributionChannel = useCallback(
    (value: DistributionChannel[]) => {
      const convertedVal = value.filter(Boolean).join(',') as DistributionChannel;

      setDistributionChannel(convertedVal);
      updateParams({
        ...targetingParams,
        distribution_channel: { op: Intersect.INTERSECTS, val: convertedVal },
        op: Criteria.ALL,
      });
    },
    [setDistributionChannel, updateParams, targetingParams]
  );

  const handleAdPlacement = useCallback(
    (value: AdPlacement[]) => {
      const convertedVal = value.filter(Boolean).join(',') as AdPlacement;
      setAdPlacement(convertedVal);

      updateParams({
        ...targetingParams,
        ad_placement: { op: Intersect.INTERSECTS, val: convertedVal },
        op: Criteria.ALL,
      });
    },
    [updateParams, setAdPlacement, targetingParams]
  );

  const renderedOption = useMemo(() => {
    switch (categoryType) {
      case InventoryFormatTypeOption.DISTRIBUTION_CHANNEL:
        return (
          <AutoComplete
            textFieldProps={{
              label: displayContext === displayContexts.TRAFFIC_SET ? t('Distribution Channel') : t('Channel'),
              margin: 'dense',
              placeholder:
                displayContext === displayContexts.TRAFFIC_SET
                  ? t('[ Select Distribution Channel ]')
                  : t('[ Select Channel ]'),
            }}
            options={distributionChannelOptionsList}
            fullWidth
            loading={isDistributionChannelLoading}
            renderOptions={{
              dataTest: 'type-option',
              tooltip: true,
            }}
            getOptionLabel={option => (option ? distributionChannelMap[option]?.name : (option ?? ''))}
            onChange={(_, value) =>
              value ? handleDistributionChannel(value as DistributionChannel[]) : setDistributionChannel(null)
            }
            value={isDistributionChannelLoading ? [] : (distributionChannel?.replaceAll(' ', '').split(',') ?? [])}
            textFieldReadOnly
            multiple
            renderValue={() => null}
            data-test="distribution-channel"
          />
        );
      case InventoryFormatTypeOption.AD_PLACEMENT:
        return (
          <AutoComplete
            textFieldProps={{ label: t('Format'), margin: 'dense', placeholder: t('[ Select Format ]') }}
            options={adPlacementOptionsList}
            fullWidth
            loading={isAdPlacementLoading}
            getOptionLabel={option => (option ? adPlacementMap[option]?.name : (option ?? ''))}
            onChange={(_, value) => (value ? handleAdPlacement(value as AdPlacement[]) : setAdPlacement(null))}
            value={isAdPlacementLoading ? [] : (adPlacement?.replaceAll(' ', '').split(',') ?? [])}
            textFieldReadOnly
            multiple
            renderValue={() => null}
            data-test="ad-placement"
          />
        );
    }
  }, [
    adPlacement,
    adPlacementMap,
    adPlacementOptionsList,
    categoryType,
    distributionChannel,
    distributionChannelMap,
    distributionChannelOptionsList,
    handleAdPlacement,
    handleDistributionChannel,
    isAdPlacementLoading,
    isDistributionChannelLoading,
    setAdPlacement,
    setDistributionChannel,
    t,
    displayContext,
  ]);

  return (
    <DrawerTopBox sx={{ paddingBottom: theme => theme.spacing(1) }}>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Grid size={4}>
          <FormatTypeGroupOptionDropdown
            value={categoryType}
            onChange={v => setCategoryType(v)}
            customOptions={customOptions}
            displayContext={displayContext}
          />
        </Grid>
        <Grid size={8}>{renderedOption}</Grid>
      </Grid>
    </DrawerTopBox>
  );
};
